"use client";

import React, { useState, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Stack,
  SelectChangeEvent,
  Alert,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Header from "@/src/features/landingPage/Header";
import Footer from "@/src/features/landingPage/Footer";
import ReCAPTCHA from "react-google-recaptcha";
import { STATE_NAME_TO_CODE } from "@/src/util/config";
import { is } from "date-fns/locale";

const FormSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(16),
  paddingBottom: theme.spacing(8),
  // Use theme background to support dark mode
  backgroundColor: (theme.vars || theme).palette.background.default,
  minHeight: "100vh",
  transition: "background-color .3s ease",
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  maxWidth: "600px",
  margin: "0 auto",
  borderRadius: theme.spacing(2),
  boxShadow:
    (theme.vars || theme).shadows?.[1] || "0 8px 40px rgba(0,0,0,0.12)",
  backgroundColor: (theme.vars || theme).palette.background.paper,
  transition: "background-color .3s ease, box-shadow .3s ease",
}));

// reCAPTCHA site key provided
const RECAPTCHA_SITE_KEY = "6Ldbz-krAAAAAExiNsi56luRuwDeqshc_iz2DByS";

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    state: "",
    email: "",
    phone: "",
    wantDemo: false,
    wantNewsletter: false,
  });

  const [checkboxError, setCheckboxError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const recaptcha = useRef<ReCAPTCHA | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear checkbox error when user makes a selection
    if (type === "checkbox" && checkboxError) {
      setCheckboxError(false);
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      state: event.target.value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Check if at least one checkbox is selected
    if (!formData.wantDemo && !formData.wantNewsletter) {
      setCheckboxError(true);
      return;
    }

    const recaptchaToken = recaptcha.current?.getValue();

    // Ensure reCAPTCHA solved
    if (!recaptchaToken) {
      setRecaptchaError("Please verify that you're not a robot.");
      return;
    }

    setCheckboxError(false);
    setIsSubmitting(true);
    setSubmitMessage(null);

    // this is where we do the submitting of the form

    try {
      // Example payload including the recaptchaToken.
      // Adjust the endpoint ("/api/contact") to match your existing backend route.
      const payload = {
        ...formData,
        recaptchaToken,
      };

      const res = await fetch(`/contactUsApi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      console.log("Response:", res);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Submission failed");
      }
      setSubmitMessage({
        type: "success",
        text: "Thank you! We'll be in touch shortly.",
      });
      setFormData({
        name: "",
        company: "",
        state: "",
        email: "",
        phone: "",
        wantDemo: false,
        wantNewsletter: false,
      });
    } catch (e) {
      console.error("Error submitting form:", e);
      setSubmitMessage({
        type: "error",
        text: "Sorry, there was an error submitting your form. Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <FormSection>
        <Container maxWidth="md">
          <FormPaper elevation={0}>
            <Stack spacing={4}>
              {submitMessage?.type === "success" ? (
                <Stack
                  spacing={3}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ textAlign: "center", minHeight: { xs: 300, md: 360 } }}
                >
                  <Typography
                    variant="h2"
                    component="p"
                    sx={(theme) => ({
                      fontSize: { xs: "2rem", md: "2.75rem" },
                      fontWeight: 700,
                      lineHeight: 1.15,
                      background: `linear-gradient(90deg, ${
                        (theme.vars || theme).palette.primary.main
                      }, ${(theme.vars || theme).palette.primary.dark})`,
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                    })}
                  >
                    {submitMessage.text}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={(theme) => ({
                      maxWidth: 560,
                      color: (theme.vars || theme).palette.text.secondary,
                      fontSize: { xs: "1rem", md: "1.15rem" },
                    })}
                  >
                    We appreciate your interest. A member of our team will
                    follow up soon.
                  </Typography>
                </Stack>
              ) : (
                <>
                  <Box sx={{ textAlign: "center", mb: 2 }}>
                    <Typography
                      variant="h2"
                      component="h2"
                      sx={(theme) => ({
                        fontSize: { xs: "1.25rem", md: "1.5rem" },
                        fontWeight: 400,
                        color: (theme.vars || theme).palette.text.secondary,
                      })}
                    >
                      Book a demo or sign up for our newsletter
                    </Typography>
                  </Box>

                  <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      {submitMessage?.type === "error" && (
                        <Alert
                          severity="error"
                          sx={{
                            mb: 2,
                            borderRadius: 2,
                          }}
                        >
                          {submitMessage.text}
                        </Alert>
                      )}

                      <TextField
                        name="name"
                        label="Name"
                        variant="filled"
                        fullWidth
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: (theme.vars || theme).palette.primary
                                .main,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: (theme.vars || theme).palette.primary
                                .main,
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: (theme.vars || theme).palette.primary.main,
                          },
                        })}
                      />

                      <TextField
                        name="email"
                        label="Email"
                        type="email"
                        variant="filled"
                        fullWidth
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: (theme.vars || theme).palette.primary
                                .main,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: (theme.vars || theme).palette.primary
                                .main,
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: (theme.vars || theme).palette.primary.main,
                          },
                        })}
                      />

                      <TextField
                        name="company"
                        label="Company Affiliation"
                        variant="filled"
                        fullWidth
                        value={formData.company}
                        onChange={handleInputChange}
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: (theme.vars || theme).palette.primary
                                .main,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: (theme.vars || theme).palette.primary
                                .main,
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: (theme.vars || theme).palette.primary.main,
                          },
                        })}
                      />

                      <FormControl variant="filled" fullWidth>
                        <InputLabel id="state-label">State</InputLabel>
                        <Select
                          labelId="state-label"
                          label="State"
                          name="state"
                          value={formData.state}
                          onChange={handleSelectChange}
                          displayEmpty
                        >
                          {Object.entries(STATE_NAME_TO_CODE).map(
                            ([name, code]) => (
                              <MenuItem key={code} value={code}>
                                {name}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>

                      <TextField
                        name="phone"
                        label="Phone Number"
                        type="tel"
                        variant="filled"
                        fullWidth
                        value={formData.phone}
                        onChange={handleInputChange}
                        sx={(theme) => ({
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor: (theme.vars || theme).palette.primary
                                .main,
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: (theme.vars || theme).palette.primary
                                .main,
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: (theme.vars || theme).palette.primary.main,
                          },
                        })}
                      />

                      <Box>
                        <Typography
                          variant="body1"
                          sx={(theme) => ({
                            mb: 2,
                            color: (theme.vars || theme).palette.text.primary,
                            fontWeight: 500,
                          })}
                        >
                          How can we help you? *
                        </Typography>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="wantDemo"
                                checked={formData.wantDemo}
                                onChange={handleInputChange}
                              />
                            }
                            label="I want a demo"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="wantNewsletter"
                                checked={formData.wantNewsletter}
                                onChange={handleInputChange}
                              />
                            }
                            label="Sign me up for the newsletter"
                          />
                        </FormGroup>
                        {checkboxError && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: (theme) =>
                                (theme.vars || theme).palette.error.main,
                              fontSize: "0.75rem",
                              mt: 1,
                              ml: 2,
                            }}
                          >
                            Please select at least one option
                          </Typography>
                        )}
                      </Box>

                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 2,
                            mb: 1,
                          }}
                        >
                          <ReCAPTCHA
                            ref={recaptcha}
                            sitekey={RECAPTCHA_SITE_KEY}
                            style={{ display: "inline-block" }}
                          />
                        </Box>
                        {recaptchaError && (
                          <Typography
                            variant="body2"
                            sx={(theme) => ({
                              color: (theme.vars || theme).palette.error.main,
                              fontSize: "0.75rem",
                              mt: -1,
                              mb: 2,
                              textAlign: "center",
                            })}
                          >
                            {recaptchaError}
                          </Typography>
                        )}
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={isSubmitting}
                          loading={isSubmitting}
                          sx={(theme) => ({
                            backgroundColor: (theme.vars || theme).palette
                              .primary.main,
                            color: (theme.vars || theme).palette.primary
                              .contrastText,
                            py: 1.5,
                            fontSize: "1.1rem",
                            fontWeight: 600,
                            textTransform: "none",
                            width: "100%",
                            "&:hover": {
                              backgroundColor: (theme.vars || theme).palette
                                .primary.dark,
                              transform: "translateY(-1px)",
                            },
                            "&:disabled": {
                              backgroundColor: (theme.vars || theme).palette
                                .action.disabledBackground,
                              color: (theme.vars || theme).palette.action
                                .disabled,
                            },
                            transition: "all 0.2s ease",
                            position: "relative",
                          })}
                        >
                          {isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </FormPaper>
        </Container>
      </FormSection>
      <Footer />
    </>
  );
};

export default ContactUs;
